import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withAnalytics, analyticsShape } from 'components/Tracking';

import * as authActions from 'actions/auth';
import * as sappaAuthActions from '_projects/sappa/actions/auth';
import * as userActions from 'actions/user';

import { PanelWrapper } from 'components';

import CreateAccountFormComponent from './CreateAccountForm';
import CreateAccountComponent from './CreateAccount';
import ConfirmEmailComponent from './ConfirmEmail';

const STEPS = {
  signup: 'signup',
  createAccount: 'createAccount',
  confirmEmail: 'confirmEmail',
};

function CreateAccountViewComponent({ analytics }) {
  const dispatch = useDispatch();
  const [step, setStep] = useState({ name: STEPS.signup });
  const [generalError, setGeneralError] = useState(null);
  const {
    brandNamePossessive,
    contactUrl,
    termsAndConditionsUrl,
    showTermsCheckbox,
  } = useSelector(state => ({
    brandNamePossessive: state.settings.brand.namePossessive,
    contactUrl: state.settings.features.contact?.url,
    termsAndConditionsUrl: state.settings.features.terms?.url,
    showTermsCheckbox: !!state.settings.features.registerMethods?.inputGroups?.find(group => group.inputFields.find(field => field.key === 'terms')),
  }));

  const failSignup = () => {
    setGeneralError('createAccount.errFailed');
  };

  const finishRegistration = async (userId, token) => {
    setGeneralError(null);
    analytics.onSignupSuccess('sappa_signup');

    await dispatch(authActions.authenticate(userId, token));
    await dispatch(userActions.getUser());
    await dispatch(authActions.openInitialView());
  };

  const createAccount = async (email, customerId, password) => {
    const response = await dispatch(sappaAuthActions.sappaSignup(customerId, password, email))
      .catch(failSignup);

    if (!response) {
      return;
    }

    if (response.status === 201) {
      const {
        userId,
        token,
      } = await response.json();

      await finishRegistration(userId, token);

      return;
    }

    failSignup();
  };

  const signupRequest = async (customerId, password) => {
    const response = await dispatch(sappaAuthActions.sappaSignup(customerId, password))
      .catch(failSignup);

    if (!response) {
      return;
    }

    if (response.status === 401) {
      setGeneralError('createAccount.errBadCredentials');
      return;
    }

    if (response.status === 412) {
      // request email and try again
      setStep({
        name: STEPS.createAccount,
        customerId,
        password,
      });
      setGeneralError(null);
      return;
    }

    if (response.status === 201) {
      // parse response and go to email confirmation
      const {
        userId,
        token,
        email,
      } = await response.json();

      setStep({
        name: STEPS.confirmEmail,
        email,
        userId,
        token,
      });
      setGeneralError(null);
      return;
    }

    failSignup();
  };

  if (step.name === STEPS.signup) {
    return (
      <PanelWrapper>
        <CreateAccountFormComponent
          showTermsCheckbox={showTermsCheckbox}
          brandNamePossessive={brandNamePossessive}
          termsAndConditionsUrl={termsAndConditionsUrl}
          error={generalError}
          onSubmit={
            ({ customerId, password }) => signupRequest(parseInt(customerId, 10), password)
          }
        />
      </PanelWrapper>
    );
  }

  if (step.name === STEPS.createAccount) {
    return (
      <PanelWrapper>
        <CreateAccountComponent
          brandNamePossessive={brandNamePossessive}
          termsAndConditionsUrl={termsAndConditionsUrl}
          error={generalError}
          onSubmit={({ email }) => createAccount(email, step.customerId, step.password)}
        />
      </PanelWrapper>
    );
  }

  if (step.name === STEPS.confirmEmail) {
    return (
      <PanelWrapper>
        <ConfirmEmailComponent
          email={step.email}
          contactUrl={contactUrl}
          onConfirm={() => finishRegistration(step.userId, step.token)}
        />
      </PanelWrapper>
    );
  }

  throw new Error(`unexpected step: ${step.name}`);
}

CreateAccountViewComponent.propTypes = {
  analytics: analyticsShape.isRequired,
};

export default withAnalytics(CreateAccountViewComponent);
